import { useState } from "react";
import { DateTime } from "luxon";
import { MonthPicker } from "@/components/ui/month-picker";
import { EngagementChart } from "./engagement-chart";
import { MonthlyBonus } from "./monthly-bonus";
import { RetentionChart } from "./retention-chart";
import { MatchRate } from "./match-rate";
import { UserTable } from "./users-table/user-table";
import {
  useGetMentorEngagementData,
  useGetMentorIncentiveData,
  useGetMentorRetentionData,
  useGetMentorProfileInterestData,
} from "@/lib/api/mentorships";
import { useSearch } from "@tanstack/react-router";
import { useCurrentUser, UserRole, useUser } from "@/lib/api/user";
import { useTrackMentorActiveTeens } from "@/lib/posthog-events";

export function MentorDataDashboard() {
  // Try to get mentor ID from URL query params, fallback to current user ID
  const { mentorId } = useSearch({ strict: false }) as { mentorId?: string };
  const { data: currentUser } = useCurrentUser();
  const targetId = mentorId || currentUser?.id || "";

  // Default to current month
  const currentDate = DateTime.now();
  const currentMonth = currentDate.month - 1; // Convert to 0-based index for consistency
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const normalizedMonth = selectedMonth + 1;

  const { data: targetUser } = useUser(targetId);

  const { data: engagementData, isPending: isMentorEngagementDataLoading } =
    useGetMentorEngagementData(targetId, normalizedMonth);
  const { data: retentionData, isPending: isMentorRetentionDataLoading } =
    useGetMentorRetentionData(targetId, normalizedMonth);
  const { data: incentiveData, isPending: isMentorIncentiveDataLoading } =
    useGetMentorIncentiveData(targetId, normalizedMonth);
  const {
    data: profileInterestData,
    isPending: isMentorProfileInterestDataLoading,
  } = useGetMentorProfileInterestData(targetId, normalizedMonth);

  useTrackMentorActiveTeens(
    engagementData?.data?.engagementData?.engagementData?.[0]?.active_teens
  );

  return (
    <div className="px-[80px] py-[36px]">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div className="mb-[32px] h-[60px]">
          <h1 className="font-avantt text-2xl font-bold leading-8 tracking-[-0.48px] text-[#1C1C1C] md:text-3xl">
            {currentUser?.role !== UserRole.MENTOR && targetUser
              ? `${targetUser?.name}'s Dashboard`
              : "Welcome back!"}
          </h1>
          <p className="font-twk-lausanne mt-[4px] text-base font-normal leading-6 text-[#777]">
            {DateTime.now()
              .set({ month: selectedMonth + 1 })
              .toFormat("MMMM yyyy")}
          </p>
        </div>
        <div>
          <MonthPicker
            month={selectedMonth}
            setMonth={setSelectedMonth}
            monthsToShow={6}
            showYear={true}
            className="w-[148px] px-[12px] py-[8px] text-center"
          />
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="mb-[40px] grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column - Charts */}
        <div className="space-y-6 lg:col-span-2">
          <EngagementChart
            engagementData={engagementData}
            isLoading={isMentorEngagementDataLoading}
          />
        </div>

        {/* Right Column - Metrics */}
        <div className="h-[710px] space-y-6">
          <MonthlyBonus
            incentiveData={incentiveData?.data?.incentiveData}
            isLoading={isMentorIncentiveDataLoading}
          />
          <RetentionChart
            retentionData={retentionData}
            isLoading={isMentorRetentionDataLoading}
          />
          <MatchRate
            profileInterestData={profileInterestData}
            isLoading={isMentorProfileInterestDataLoading}
          />
        </div>
      </div>

      {/* User Table */}
      <UserTable selectedMonth={normalizedMonth} defaultPageSize={30} />
    </div>
  );
}
